import axios, { AxiosResponse } from 'axios'
import { apiNode } from './api'
import type {
	CancelESignObjectParamsProps,
	CreateESignForVersionDataResponse,
	CreateESignForVersionParamsProps,
	DeclineESignObjectParamsProps,
	DeclinePublicESignObjectParamsProps,
	RecordPublicESignConsentParamsProps,
	DeleteESignObjectParamsProps,
	ESignLinkDataResponse,
	ESignLinkPreviewDataResponse,
	ESignField,
	ESignObject,
	ESignObjectItem,
	ESignRecipient,
	GetESignLinkSignedContentParamsProps,
	GetESignObjectParamsProps,
	GetESignLinkParamsProps,
	GetESignLinkPreviewParamsProps,
	GetESignObjectsDataResponse,
	GetESignObjectsForFileParamsProps,
	GetESignObjectsParamsProps,
	GetESignSignedContentParamsProps,
	PublicESignObjectDataResponse,
	ESignRecipientRole,
	ESignRecipientStatus,
	ESignStatus,
	SendESignObjectForSigningParamsProps,
	SetESignObjectContentParamsProps,
	SignESignObjectParamsProps,
	SignPublicESignObjectParamsProps,
} from './types/esign.interfaces'

const API_ESIGNS = '2/esigns'
const API_ESIGN_LINKS = '2/esign-links'
const API_FILES = '2/files/v2/files'

const publicESignAuthConfig = (token: string) => ({
	headers: {
		'X-ESign-Token': token.replace(/"/g, ''),
	},
	withCredentials: false as const,
})

const ESIGN_STATUS_BY_CODE: Record<string, ESignStatus> = {
	'0': 'DRAFT',
	'1': 'SENT',
	'2': 'PARTIALLY_SIGNED',
	'6': 'SIGNED_FILE_GENERATING',
	'3': 'SIGNED',
	'4': 'DECLINED',
	'5': 'CANCELED',
}

const ESIGN_STATUSES = new Set<ESignStatus>([
	'DRAFT',
	'SENT',
	'PARTIALLY_SIGNED',
	'SIGNED_FILE_GENERATING',
	'SIGNED',
	'DECLINED',
	'CANCELED',
])

const ESIGN_RECIPIENT_STATUS_BY_CODE: Record<string, ESignRecipientStatus> = {
	'0': 'PENDING',
	'1': 'SIGNED',
	'2': 'DECLINED',
	'3': 'CANCELED',
}

const ESIGN_RECIPIENT_STATUSES = new Set<ESignRecipientStatus>([
	'PENDING',
	'SIGNED',
	'DECLINED',
	'CANCELED',
])

const ESIGN_RECIPIENT_ROLE_BY_CODE: Record<string, ESignRecipientRole> = {
	'0': 'SIGNER',
	'1': 'VIEWER',
}

const ESIGN_RECIPIENT_ROLES = new Set<ESignRecipientRole>(['SIGNER', 'VIEWER'])

function unwrapPayload<T = any>(payload: any): T {
	return (payload?.data ?? payload) as T
}

function normalizeESignStatus(status: unknown): ESignStatus {
	const normalizedValue = String(status ?? '').toUpperCase()
	if (ESIGN_STATUSES.has(normalizedValue as ESignStatus)) {
		return normalizedValue as ESignStatus
	}

	return ESIGN_STATUS_BY_CODE[normalizedValue] ?? 'DRAFT'
}

function normalizeRecipientStatus(
	status: unknown,
): ESignRecipientStatus | null {
	if (status === null || status === undefined || status === '') return null

	const normalizedValue = String(status ?? '').toUpperCase()
	if (ESIGN_RECIPIENT_STATUSES.has(normalizedValue as ESignRecipientStatus)) {
		return normalizedValue as ESignRecipientStatus
	}

	return ESIGN_RECIPIENT_STATUS_BY_CODE[normalizedValue] ?? 'PENDING'
}

function normalizeRecipientRole(role: unknown): ESignRecipientRole {
	const normalizedValue = String(role ?? '').toUpperCase()
	if (ESIGN_RECIPIENT_ROLES.has(normalizedValue as ESignRecipientRole)) {
		return normalizedValue as ESignRecipientRole
	}
	if (normalizedValue === 'CC') return 'VIEWER'

	return ESIGN_RECIPIENT_ROLE_BY_CODE[normalizedValue] ?? 'SIGNER'
}

function normalizeFlowMode(flowMode: unknown): 'SEQUENTIAL' | 'PARALLEL' {
	const normalizedValue = String(flowMode ?? '').toUpperCase()
	return normalizedValue === 'PARALLEL' ? 'PARALLEL' : 'SEQUENTIAL'
}

function normalizeRecipient(recipient: any): ESignRecipient {
	return {
		id: recipient?.id ? String(recipient.id) : undefined,
		name: recipient?.name ?? '',
		email: recipient?.email ?? '',
		role: normalizeRecipientRole(recipient?.role),
		signingOrder:
			typeof recipient?.signingOrder === 'number'
				? recipient.signingOrder
				: null,
		status: normalizeRecipientStatus(recipient?.status),
	}
}

function normalizeField(field: any): ESignField {
	return {
		id: field?.id ? String(field.id) : undefined,
		recipientId: field?.recipientId ? String(field.recipientId) : null,
		type: String(field?.type ?? 'TEXT').toUpperCase() as ESignField['type'],
		page: Number(field?.page ?? field?.pageNumber ?? 1),
		x: Number(field?.x ?? field?.pageX ?? 0),
		y: Number(field?.y ?? field?.pageY ?? 0),
		width: Number(field?.width ?? field?.pageWidth ?? 0),
		height: Number(field?.height ?? field?.pageHeight ?? 0),
		required: Boolean(field?.required),
		value: field?.value ?? null,
		label: field?.label ?? null,
		placeholder: field?.placeholder ?? null,
		meta:
			field?.meta && typeof field.meta === 'object'
				? (field.meta as Record<string, unknown>)
				: null,
	}
}

function normalizeESignObjectResponse(payload: any): ESignObject {
	const unwrapped = unwrapPayload<any>(payload)
	const eSign = unwrapped?.eSign ?? unwrapped
	const recipients = Array.isArray(unwrapped?.recipients)
		? unwrapped.recipients
		: Array.isArray(eSign?.recipients)
			? eSign.recipients
			: []
	const fields = Array.isArray(unwrapped?.fields)
		? unwrapped.fields
		: Array.isArray(eSign?.fields)
			? eSign.fields
			: []

	return {
		id: String(eSign?.id ?? ''),
		fileId: String(eSign?.fileId ?? ''),
		versionId: eSign?.fileVersionId ?? eSign?.versionId ?? null,
		sourceESignObjectId:
			eSign?.sourceESignId ?? eSign?.sourceESignObjectId ?? null,
		title: eSign?.title ?? eSign?.name ?? '',
		message: eSign?.message ?? null,
		flowMode: normalizeFlowMode(eSign?.flowMode),
		status: normalizeESignStatus(eSign?.status),
		recipients: recipients.map(normalizeRecipient),
		fields: fields.map(normalizeField),
		createdAt: eSign?.createdAt ?? undefined,
		completedAt: eSign?.completedAt ?? null,
		updatedAt:
			eSign?.updatedAt ??
			eSign?.sentAt ??
			eSign?.completedAt ??
			eSign?.declinedAt ??
			eSign?.createdAt ??
			undefined,
	}
}

function normalizeESignObjectItem(payload: any): ESignObjectItem {
	const eSignObject = normalizeESignObjectResponse(payload)

	return {
		id: eSignObject.id,
		fileId: eSignObject.fileId,
		versionId: eSignObject.versionId ?? null,
		sourceESignObjectId: eSignObject.sourceESignObjectId ?? null,
		title: eSignObject.title,
		status: eSignObject.status,
		recipients: eSignObject.recipients,
		fieldsCount: eSignObject.fields.length,
		createdAt: eSignObject.createdAt,
		updatedAt: eSignObject.updatedAt,
	}
}

function normalizeESignObjectsListResponse(
	payload: any,
): GetESignObjectsDataResponse {
	const unwrapped = unwrapPayload<any>(payload)

	if (Array.isArray(unwrapped)) {
		return {
			items: unwrapped.map(normalizeESignObjectItem),
			total: unwrapped.length,
		}
	}

	if (Array.isArray(unwrapped?.items)) {
		return {
			items: unwrapped.items.map(normalizeESignObjectItem),
			total:
				typeof unwrapped.total === 'number'
					? unwrapped.total
					: unwrapped.items.length,
		}
	}

	return {
		items: [],
		total: 0,
	}
}

function normalizePublicESignObjectResponse(
	payload: any,
	recipientId?: string | null,
): PublicESignObjectDataResponse {
	const eSignObject = normalizeESignObjectResponse(payload)
	const recipient =
		(recipientId &&
			eSignObject.recipients.find(
				(item) => String(item.id) === String(recipientId),
			)) ||
		eSignObject.recipients.find((item) => item.role === 'SIGNER') ||
		eSignObject.recipients[0] ||
		null

	const recipientFields = recipient?.id
		? eSignObject.fields.filter(
				(field) => String(field.recipientId) === String(recipient.id),
			)
		: eSignObject.fields

	const recipientStatus = String(recipient?.status ?? '').toUpperCase()
	const canSign =
		['SENT', 'PARTIALLY_SIGNED'].includes(
			String(eSignObject.status ?? '').toUpperCase(),
		) &&
		(!recipientStatus || recipientStatus === 'PENDING')
	const canDecline =
		['SENT', 'PARTIALLY_SIGNED'].includes(
			String(eSignObject.status ?? '').toUpperCase(),
		) &&
		(!recipientStatus || recipientStatus === 'PENDING')

	return {
		eSignObject,
		recipient,
		recipientFields,
		canSign,
		canDecline,
	}
}

function normalizeESignLinkResponse(payload: any): ESignLinkDataResponse {
	const unwrapped = unwrapPayload<any>(payload)
	const normalizedRecipient = unwrapped?.recipient
		? normalizeRecipient(unwrapped.recipient)
		: null
	const normalizedFields = Array.isArray(unwrapped?.fields)
		? unwrapped.fields.map(normalizeField)
		: []
	const eSignObject = normalizeESignObjectResponse({
		eSign: unwrapped?.eSign,
		recipients: normalizedRecipient ? [normalizedRecipient] : [],
		fields: normalizedFields,
	})

	return {
		eSignId: String(unwrapped?.eSignId ?? eSignObject.id ?? ''),
		recipientId: String(
			unwrapped?.recipientId ?? normalizedRecipient?.id ?? '',
		),
		eSignObject,
		recipient: normalizedRecipient,
		recipientFields: normalizedFields,
		file:
			unwrapped?.file && typeof unwrapped.file === 'object'
				? {
						id: String(unwrapped.file.id ?? ''),
						versionId: unwrapped.file.versionId
							? String(unwrapped.file.versionId)
							: null,
						name: String(unwrapped.file.name ?? ''),
					}
				: null,
		canSign: Boolean(unwrapped?.canSign),
		canDecline:
			typeof unwrapped?.canDecline === 'boolean'
				? unwrapped.canDecline
				: Boolean(unwrapped?.canSign),
		consentTextVersion: unwrapped?.consentTextVersion
			? String(unwrapped.consentTextVersion)
			: null,
		consentText: unwrapped?.consentText ? String(unwrapped.consentText) : null,
		erdVersionDisplayed: unwrapped?.erdVersionDisplayed
			? String(unwrapped.erdVersionDisplayed)
			: null,
		erdUrlDisplayed: unwrapped?.erdUrlDisplayed
			? String(unwrapped.erdUrlDisplayed)
			: null,
		erdContent: unwrapped?.erdContent ? String(unwrapped.erdContent) : null,
	}
}

export const ESignService = {
	async createESignForVersion({
		fileId,
		versionId,
		title,
		message,
		flowMode,
		name,
		sourceESignId,
	}: CreateESignForVersionParamsProps): Promise<CreateESignForVersionDataResponse> {
		const resolvedTitle = title ?? name
		const response: AxiosResponse<{ data: CreateESignForVersionDataResponse }> =
			await apiNode.post(
				`${API_FILES}/${fileId}/versions/${versionId}/esigns`,
				{
					sourceESignId,
					title: resolvedTitle,
					message,
					flowMode,
				},
			)

		return response.data.data
	},

	async getESignObjects({
		status,
		statuses,
	}: GetESignObjectsParamsProps): Promise<GetESignObjectsDataResponse> {
		const response: AxiosResponse<any> = await apiNode.get(`${API_ESIGNS}`, {
			params: {
				status: status ?? statuses?.[0],
			},
		})

		return normalizeESignObjectsListResponse(response.data)
	},

	async getESignLink({
		token,
	}: GetESignLinkParamsProps): Promise<ESignLinkDataResponse> {
		const response: AxiosResponse<any> = await apiNode.get(
			`${API_ESIGN_LINKS}/current`,
			publicESignAuthConfig(token),
		)

		return normalizeESignLinkResponse(response.data)
	},

	async getESignLinkPreview({
		token,
	}: GetESignLinkPreviewParamsProps): Promise<ESignLinkPreviewDataResponse> {
		const response: AxiosResponse<ESignLinkPreviewDataResponse> =
			await apiNode.get(
				`${API_ESIGN_LINKS}/preview`,
				publicESignAuthConfig(token),
			)

		return response.data
	},

	async getESignObjectsForFile({
		fileId,
		status,
		onlyMyRecipients,
	}: GetESignObjectsForFileParamsProps): Promise<GetESignObjectsDataResponse> {
		const response: AxiosResponse<any> = await apiNode.get(
			`${API_FILES}/${fileId}/esigns`,
			{
				params: {
					status,
					onlyMyRecipients,
				},
			},
		)

		return normalizeESignObjectsListResponse(response.data)
	},

	async getESignObject({
		eSignObjectId,
	}: GetESignObjectParamsProps): Promise<ESignObject> {
		const response: AxiosResponse<any> = await apiNode.get(
			`${API_ESIGNS}/${eSignObjectId}`,
			{},
		)

		return normalizeESignObjectResponse(response.data)
	},

	async getESignSignedContent({
		eSignObjectId,
	}: GetESignSignedContentParamsProps): Promise<ESignLinkPreviewDataResponse> {
		const response: AxiosResponse<ESignLinkPreviewDataResponse> =
			await apiNode.get(`${API_ESIGNS}/${eSignObjectId}/signed-content`)

		return response.data
	},

	async getESignLinkSignedContent({
		token,
	}: GetESignLinkSignedContentParamsProps): Promise<ESignLinkPreviewDataResponse> {
		const response: AxiosResponse<ESignLinkPreviewDataResponse> =
			await apiNode.get(
				`${API_ESIGN_LINKS}/signed-content`,
				publicESignAuthConfig(token),
			)

		return response.data
	},

	async setESignObjectContent({
		eSignObjectId,
		data,
	}: SetESignObjectContentParamsProps): Promise<ESignObject> {
		const payload = {
			...data,
			recipients: Array.isArray(data.recipients)
				? data.recipients.map((recipient) => ({
						...recipient,
						role: normalizeRecipientRole(recipient.role),
					}))
				: data.recipients,
		}
		const response: AxiosResponse<any> = await apiNode.put(
			`${API_ESIGNS}/${eSignObjectId}`,
			payload,
			{},
		)

		return normalizeESignObjectResponse(response.data)
	},

	async sendESignObjectForSigning({
		eSignObjectId,
	}: SendESignObjectForSigningParamsProps): Promise<void> {
		await apiNode.post(`${API_ESIGNS}/${eSignObjectId}/send`, {}, {})
	},

	async cancelESignObject({
		eSignObjectId,
	}: CancelESignObjectParamsProps): Promise<void> {
		await apiNode.post(`${API_ESIGNS}/${eSignObjectId}/cancel`, {}, {})
	},

	async deleteESignObject({
		eSignObjectId,
	}: DeleteESignObjectParamsProps): Promise<void> {
		await apiNode.delete(`${API_ESIGNS}/${eSignObjectId}`)
	},

	async signESignObject({
		eSignObjectId,
		recipientId,
		fields,
	}: SignESignObjectParamsProps): Promise<ESignObject> {
		const response: AxiosResponse<any> = await apiNode.post(
			`${API_ESIGNS}/${eSignObjectId}/sign`,
			{
				recipientId,
				fields,
			},
			{},
		)

		return normalizeESignObjectResponse(response.data)
	},

	async declineESignObject({
		eSignObjectId,
		recipientId,
	}: DeclineESignObjectParamsProps): Promise<ESignObject> {
		const response: AxiosResponse<any> = await apiNode.post(
			`${API_ESIGNS}/${eSignObjectId}/decline`,
			{
				recipientId,
			},
			{},
		)

		return normalizeESignObjectResponse(response.data)
	},

	async signPublicESignObject({
		eSignObjectId,
		recipientId,
		fields,
		token,
	}: SignPublicESignObjectParamsProps): Promise<PublicESignObjectDataResponse> {
		const response: AxiosResponse<any> = await apiNode.post(
			`${API_ESIGNS}/${eSignObjectId}/sign`,
			{
				recipientId,
				fields,
			},
			publicESignAuthConfig(token),
		)

		return normalizePublicESignObjectResponse(response.data, recipientId)
	},

	async recordPublicESignConsent({
		eSignObjectId,
		recipientId,
		token,
		consentTextVersion,
		erdVersionDisplayed,
		erdUrlDisplayed,
		erdOpened,
	}: RecordPublicESignConsentParamsProps): Promise<void> {
		await apiNode.post(
			`${API_ESIGNS}/${eSignObjectId}/electronic-consent`,
			{
				recipientId,
				consentTextVersion,
				erdVersionDisplayed,
				erdUrlDisplayed,
				erdOpened,
			},
			publicESignAuthConfig(token),
		)
	},

	async declinePublicESignObject({
		eSignObjectId,
		recipientId,
		token,
	}: DeclinePublicESignObjectParamsProps): Promise<PublicESignObjectDataResponse> {
		const response: AxiosResponse<any> = await apiNode.post(
			`${API_ESIGNS}/${eSignObjectId}/decline`,
			{
				recipientId,
			},
			publicESignAuthConfig(token),
		)

		return normalizePublicESignObjectResponse(response.data, recipientId)
	},

	/** Fetches public legal/ERD page HTML directly (no backend proxy). */
	async getPublicLegalPageHtml(pageUrl: string): Promise<string> {
		const { data } = await axios.get<string>(pageUrl, {
			responseType: 'text',
			headers: { Accept: 'text/html,application/xhtml+xml' },
		})
		return data
	},
}
